import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const TONE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", denied: "bg-slate-200 text-slate-700" };

export const RefundRequests = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/marketplace/refunds").then((r) => setRows(r.data.refunds)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, approve) => {
    if (approve && !window.confirm(`Approve the $${r.amount.toFixed(2)} refund for ${r.mlo_email}? The lead is removed from their account. No money is sent through Stripe - you pay it back yourself.`)) return;
    try { await api.post(`/admin/marketplace/refunds/${r.id}`, { approve }); toast.success(approve ? "Refund approved - remember to send the money" : "Refund denied"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!rows) return null;
  return (
    <Card className="mb-5 p-6" data-testid="refund-requests-card">
      <h3 className="font-semibold text-slate-900">Refund requests</h3>
      <p className="mt-1 text-sm text-slate-500">Approving removes the lead from the MLO's account. Nothing is refunded through Stripe; send approved refunds yourself.</p>
      {rows.length === 0 && <p className="mt-3 text-sm text-slate-400" data-testid="refunds-empty">No refund requests yet.</p>}
      <div className="mt-2 divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 py-3" data-testid={`refund-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div><b>{r.mlo_name || r.mlo_email}</b> <span className="text-sm text-slate-500">{r.mlo_email}</span></div>
              <div className="text-sm text-slate-600">Lead: {r.lead_name || "—"} · ${r.amount.toFixed(2)} · {new Date(r.created_at).toLocaleDateString()}</div>
              <div className="mt-1 break-words text-sm text-slate-700">"{r.reason}"</div>
            </div>
            {r.status === "pending" ? (
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => decide(r, true)} data-testid={`refund-approve-${r.id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => decide(r, false)} data-testid={`refund-deny-${r.id}`}>Deny</Button>
              </div>
            ) : <Badge className={`border-0 capitalize ${TONE[r.status]}`} data-testid={`refund-decided-${r.id}`}>{r.status}</Badge>}
          </div>
        ))}
      </div>
    </Card>
  );
};
